import api from './api';

export type QuickGameQuestion = {
  id: number;
  statement: string;
  familyName: string;
  answers: Array<{ id: number; text: string }>;
  correct_answer_id: number;
};

export type QuickGameScore = {
  player: string;
  totalCorrect: number;
  totalQuestions: number;
  totalTimeSeconds: number;
  finishedAt: string;
};

export type QuickGameSession = {
  sessionId: string;
  questions: QuickGameQuestion[];
};

const STORAGE_KEY = 'quickGameLeaderboard';

const fallbackQuestions: QuickGameQuestion[] = [
  {
    id: 1,
    statement: 'Qual é a finalidade principal do suitability exigido pela CVM?',
    familyName: 'CGA',
    answers: [
      { id: 11, text: 'Mapear objetivos e perfil do investidor' },
      { id: 12, text: 'Definir taxas de administração' },
      { id: 13, text: 'Calcular variação do CDI' },
      { id: 14, text: 'Registrar operações na B3' },
    ],
    correct_answer_id: 11,
  },
  {
    id: 2,
    statement: 'Em renda fixa, qual fator impacta diretamente o duration?',
    familyName: 'CPA-20',
    answers: [
      { id: 21, text: 'Prazo médio dos fluxos de caixa' },
      { id: 22, text: 'Dividend yield' },
      { id: 23, text: 'Beta do ativo' },
      { id: 24, text: 'Lucro por ação' },
    ],
    correct_answer_id: 21,
  },
  {
    id: 3,
    statement: 'Qual índice mede a inflação oficial no Brasil?',
    familyName: 'CEA',
    answers: [
      { id: 31, text: 'IPCA' },
      { id: 32, text: 'Ibovespa' },
      { id: 33, text: 'ICO2' },
      { id: 34, text: 'CDS' },
    ],
    correct_answer_id: 31,
  },
  {
    id: 4,
    statement: 'Qual documento formaliza a contratação de um COE?',
    familyName: 'CFP',
    answers: [
      { id: 41, text: 'Termo de Adesão' },
      { id: 42, text: 'Nota Fiscal' },
      { id: 43, text: 'Debênture' },
      { id: 44, text: 'Título de crédito' },
    ],
    correct_answer_id: 41,
  },
  {
    id: 5,
    statement: 'No mercado futuro, qual é o objetivo do ajuste diário?',
    familyName: 'CNPI',
    answers: [
      { id: 51, text: 'Mitigar risco de inadimplência' },
      { id: 52, text: 'Definir taxa de câmbio' },
      { id: 53, text: 'Calcular dividendos' },
      { id: 54, text: 'Garantir liquidez de debêntures' },
    ],
    correct_answer_id: 51,
  },
  {
    id: 6,
    statement: 'Qual indicador avalia a sensibilidade de um título prefixado?',
    familyName: 'ANCORD',
    answers: [
      { id: 61, text: 'Duration' },
      { id: 62, text: 'Alfa' },
      { id: 63, text: 'Sharpe' },
      { id: 64, text: 'Sortino' },
    ],
    correct_answer_id: 61,
  },
  {
    id: 7,
    statement: 'Qual produto possui garantia do FGC até R$ 250 mil?',
    familyName: 'CPA-10',
    answers: [
      { id: 71, text: 'LCI' },
      { id: 72, text: 'Debênture' },
      { id: 73, text: 'Letra Financeira' },
      { id: 74, text: 'CRI' },
    ],
    correct_answer_id: 71,
  },
];

const fallbackLeaderboard: QuickGameScore[] = [
  { player: 'Carla Lima', totalCorrect: 5, totalQuestions: 5, totalTimeSeconds: 12.4, finishedAt: new Date().toISOString() },
  { player: 'Bruno Peixoto', totalCorrect: 5, totalQuestions: 5, totalTimeSeconds: 13.1, finishedAt: new Date().toISOString() },
  { player: 'Ana Nogueira', totalCorrect: 4, totalQuestions: 5, totalTimeSeconds: 15.0, finishedAt: new Date().toISOString() },
];

function shuffle<T>(list: T[]): T[] {
  const arr = [...list];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

function getLocalLeaderboard(): QuickGameScore[] {
  if (typeof window === 'undefined') return fallbackLeaderboard;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return fallbackLeaderboard;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed as QuickGameScore[];
    return fallbackLeaderboard;
  } catch (error) {
    return fallbackLeaderboard;
  }
}

function saveLocalLeaderboard(scores: QuickGameScore[]) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(scores));
  } catch (error) {
    // ignore storage errors
  }
}

function sortLeaderboard(scores: QuickGameScore[]) {
  return [...scores].sort((a, b) => {
    if (a.totalCorrect !== b.totalCorrect) return b.totalCorrect - a.totalCorrect;
    if (a.totalTimeSeconds !== b.totalTimeSeconds) return a.totalTimeSeconds - b.totalTimeSeconds;
    return new Date(a.finishedAt).getTime() - new Date(b.finishedAt).getTime();
  }).slice(0, 10);
}

export async function startQuickGame(): Promise<QuickGameSession> {
  try {
    const { data } = await api.post('quick-game/start');
    if (data?.sessionId && Array.isArray(data?.questions)) {
      return {
        sessionId: data.sessionId,
        questions: data.questions as QuickGameQuestion[],
      };
    }
    throw new Error('invalid-response');
  } catch (error) {
    const questions = shuffle(fallbackQuestions).slice(0, 5);
    return {
      sessionId: `local-${Date.now()}`,
      questions,
    };
  }
}

export async function fetchQuickGameLeaderboard(): Promise<QuickGameScore[]> {
  try {
    const { data } = await api.get('quick-game/leaderboard');
    if (Array.isArray(data)) return sortLeaderboard(data as QuickGameScore[]);
    throw new Error('invalid-response');
  } catch (error) {
    return sortLeaderboard(getLocalLeaderboard());
  }
}

export async function submitQuickGameResult(score: QuickGameScore): Promise<QuickGameScore[]> {
  try {
    const { data } = await api.post('quick-game/finish', score);
    if (Array.isArray(data)) return sortLeaderboard(data as QuickGameScore[]);
    throw new Error('invalid-response');
  } catch (error) {
    const updated = sortLeaderboard([...getLocalLeaderboard(), score]);
    saveLocalLeaderboard(updated);
    return updated;
  }
}

